import { useId, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import type { PromptCode } from '@openclassrooms/js-ai-sdk';

import { clientApi } from '../../configuration';

import type {
  AIServerName,
  SDKPromptParameters,
  SDKPromptOptions,
  UsePromptReturn,
  AIResponse,
} from '../interfaces';

export type { UsePromptReturn, SDKPromptParameters };

export type UsePromptParams = {
  promptCode: PromptCode;
  serverName: AIServerName;
  shouldStream?: boolean;
};

export default function usePrompt<
  PromptParams extends SDKPromptParameters | undefined = undefined
>({
  promptCode,
  serverName,
  shouldStream = false,
}: UsePromptParams): UsePromptReturn<PromptParams> {
  const hookId = useId();
  const queryKey = ['prompt', hookId];

  const queryClient = useQueryClient();
  const { data } = useQuery<AIResponse>({
    queryKey,
    queryFn: async () => '',
  });

  const { mutate, error, isPending } = useMutation<
    void,
    { status: number },
    { input: string; parameters?: PromptParams }
  >({
    mutationKey: queryKey,
    mutationFn: async ({
      input,
      parameters,
    }: {
      input: string;
      parameters?: SDKPromptOptions['parameters'];
    }) => {
      const aiClient = clientApi.getAIClient(serverName);

      return new Promise((resolve, reject) => {
        const chunks: string[] = [];

        const onStreamData = shouldStream
          ? (chunk: string) => {
              chunks.push(chunk);
              queryClient.setQueryData<string>(queryKey, chunks.join(''));
            }
          : undefined;

        aiClient
          .prompt(promptCode, {
            input,
            parameters,
            onStreamData,
          })
          .then((response) => {
            if (shouldStream) {
              resolve(chunks.join(''));
            } else {
              resolve(response);
            }
          })
          .catch(reject);
      });
    },
    onSuccess(response) {
      queryClient.setQueryData(queryKey, response);
    },
  });

  const prompt: UsePromptReturn<PromptParams>['prompt'] = useCallback(
    (input, parameters, options = {}) => {
      mutate(
        {
          input,
          parameters,
        },
        options
      );
    },
    [mutate]
  );

  const hookReturnValue: Pick<UsePromptReturn<PromptParams>, 'data' | 'isFetching' | 'prompt'> = {
    data: data ?? '',
    isFetching: isPending,
    prompt,
  };

  return error === null
    ? {
        ...hookReturnValue,
        hasError: false,
        isLimitReached: false,
      }
    : {
        ...hookReturnValue,
        hasError: true,
        errorStatusCode: error.status,
        isLimitReached: error.status === 429,
      };
}
